// src/firebase/gameEngine.js
// Pure game-logic functions that compute new state and write to Firebase.
// No React imports — these are async functions called from the App.

import {
  dbSet, dbGet, dbUpdate, dbPush, dbRemove, dbListen,
} from "./firebase.js";
import {
  uid, shuffle, resolveMajority, checkWinCondition,
} from "../utils/helpers.js";
import {
  freshRoomState, freshPlayer,
  VOTE_DURATION, DOCTOR_SKIP_DELAY, PHASES, ROLES,
} from "../utils/constants.js";

// ── helpers local to this module ──────────────────────────────
const roomPath  = (code)           => `rooms/${code}`;
const statePath = (code)           => `rooms/${code}/state`;
const chatPath  = (code, channel)  => `rooms/${code}/chat/${channel}`;

async function pushState(code, st) {
  return dbSet(statePath(code), st);
}

async function pushChat(code, channel, author, text, role = "system") {
  const type = author === "System" ? "system" : "player";
  return dbPush(chatPath(code, channel), {
    author, text, role, type, ts: Date.now(),
  });
}

// ══════════════════════════════════════════════════════════════
// ROOM CREATE / JOIN / RESET
// ══════════════════════════════════════════════════════════════

export async function createRoom(playerId, playerName) {
  const { genCode } = await import("../utils/helpers.js");
  const code   = genCode();
  const player = freshPlayer(playerId, playerName, true);
  const state  = freshRoomState([player]);
  await dbSet(roomPath(code), { state });
  return { code, state };
}

export async function joinRoom(code, playerId, playerName) {
  const snap = await dbGet(statePath(code));
  if (!snap.exists()) return { error: "Room not found. Check the code." };

  const existing = snap.val();
  existing.confirmedVotes = existing.confirmedVotes || [];
  existing.revealReady    = existing.revealReady    || [];

  // Rejoin with same name
  const existingPlayer = (existing.players || []).find(
    (p) => p.name.toLowerCase() === playerName.toLowerCase(),
  );
  if (existingPlayer) {
    return { state: existing, me: existingPlayer, rejoined: true };
  }

  if (existing.phase !== PHASES.LOBBY) {
    return { error: "Game in progress. Rejoin with your original name." };
  }

  const nameTaken = (existing.players || []).some(
    (p) => p.name.toLowerCase() === playerName.toLowerCase(),
  );
  if (nameTaken) return { error: "Name already taken — choose another." };

  const player       = freshPlayer(playerId, playerName, false);
  const updatedPlayers = [...(existing.players || []), player];
  await dbUpdate(statePath(code), { players: updatedPlayers });
  return { state: { ...existing, players: updatedPlayers }, me: player, rejoined: false };
}

export async function resetRoom(code, currentState) {
  const fresh = freshRoomState(
    (currentState.players || []).map((p) =>
      freshPlayer(p.id, p.name, p.isHost || false),
    ),
    currentState.lobbyMafia  || 1,
    currentState.lobbyDoctor || 1,
  );
  // Atomically rewrite the whole room — all clients' onValue fires with phase:'lobby'
  await dbSet(roomPath(code), { state: fresh });
  // Clear chat for new game
  await dbRemove(`rooms/${code}/chat`);
  return fresh;
}

export async function deleteRoom(code) {
  return dbRemove(roomPath(code));
}

// ══════════════════════════════════════════════════════════════
// LOBBY CONFIG
// ══════════════════════════════════════════════════════════════

export async function updateLobbyConfig(code, lobbyMafia, lobbyDoctor) {
  return dbUpdate(statePath(code), { lobbyMafia, lobbyDoctor });
}

// ══════════════════════════════════════════════════════════════
// START GAME — role assignment with cryptographically better shuffle
// ══════════════════════════════════════════════════════════════

export async function startGame(code, state) {
  const m     = state.lobbyMafia  || 1;
  const d     = state.lobbyDoctor || 1;
  const total = (state.players || []).length;

  if (total < 5)          return { error: "Need at least 5 players." };
  if (total - m - d < 1) return { error: "Not enough citizens. Reduce Mafia or Doctor count." };

  // Build roles array then do MULTIPLE shuffles for true randomness
  let roles = [
    ...Array(m).fill(ROLES.MAFIA),
    ...Array(d).fill(ROLES.DOCTOR),
    ...Array(total - m - d).fill(ROLES.CITIZEN),
  ];
  // Triple shuffle for extra randomness
  roles = shuffle(shuffle(shuffle(roles)));

  const players = state.players.map((p, i) => ({
    ...p,
    role:         roles[i],
    alive:        true,
    roleRevealed: false,
  }));

  const newState = {
    ...state,
    players,
    phase:             PHASES.REVEAL,
    round:             1,
    mafiaVotes:        {},
    doctorVotes:       {},
    dayVotes:          {},
    confirmedVotes:    [],
    skippedVotes:      [],
    nightKilled:       null,
    savedId:           null,
    lastAnnouncement:  null,
    announcementId:    null,
    dayAnnouncement:   null,
    dayAnnouncementId: null,
    winner:            null,
    revealReady:       [],
    timerEndsAt:       null,
    timerDuration:     VOTE_DURATION,
    doctorSkipAt:      null,
  };

  await pushState(code, newState);
  return { state: newState };
}

// ══════════════════════════════════════════════════════════════
// REVEAL READY
// ══════════════════════════════════════════════════════════════

export async function markReady(code, currentState, playerId) {
  const ready = [...(currentState.revealReady || [])];
  if (ready.includes(playerId)) return;
  ready.push(playerId);
  await dbUpdate(statePath(code), { revealReady: ready });
}

// ══════════════════════════════════════════════════════════════
// HOST ADVANCE PHASE  (the main state machine)
// ══════════════════════════════════════════════════════════════

export async function hostAdvancePhase(code, state) {
  const phase = state.phase;

  // ── reveal / result → night ──────────────────────────────
  if (phase === PHASES.REVEAL || phase === PHASES.RESULT) {
    const round   = phase === PHASES.RESULT ? state.round + 1 : state.round;
    const endsAt  = Date.now() + VOTE_DURATION * 1000;
    const newState = {
      ...state,
      phase:             PHASES.NIGHT,
      round,
      mafiaVotes:        {},
      doctorVotes:       {},
      dayVotes:          {},
      confirmedVotes:    [],
      skippedVotes:      [],
      nightKilled:       null,
      savedId:           null,
      lastAnnouncement:  null,
      announcementId:    null,
      dayAnnouncement:   null,
      dayAnnouncementId: null,
      timerEndsAt:       endsAt,
      timerDuration:     VOTE_DURATION,
      doctorSkipAt:      null,
    };
    await pushState(code, newState);
    await pushChat(code, "all",   "System", `🌙 Round ${round} begins. The night falls over the town...`);
    await pushChat(code, "night", "System", "🔪 Mafia — choose your target for tonight.");
    return newState;
  }

  // ── night → save ─────────────────────────────────────────
  if (phase === PHASES.NIGHT) {
    const aliveMafia     = (state.players || []).filter((p) => p.alive && p.role === ROLES.MAFIA);
    const killed         = resolveMajority(
      state.mafiaVotes || {},
      aliveMafia.map((p) => p.id),
    );
    const aliveDoctors   = (state.players || []).filter((p) => p.alive && p.role === ROLES.DOCTOR);
    const endsAt         = Date.now() + VOTE_DURATION * 1000;

    // If NO doctors alive → set doctorSkipAt so clients skip after 10 s
    const doctorSkipAt = aliveDoctors.length === 0
      ? Date.now() + DOCTOR_SKIP_DELAY * 1000
      : null;

    const newState = {
      ...state,
      nightKilled:  killed,
      phase:        PHASES.SAVE,
      timerEndsAt:  endsAt,
      timerDuration: VOTE_DURATION,
      doctorVotes:  {},
      confirmedVotes: [],
      skippedVotes: [],
      doctorSkipAt,
    };
    await pushState(code, newState);

    if (aliveDoctors.length === 0) {
      await pushChat(code, "save", "System", "💊 The doctor channel is quiet tonight...");
    } else {
      await pushChat(code, "save", "System",
        killed
          ? "💊 Doctor — someone has been targeted tonight. Choose who to save."
          : "💊 Doctor — the Mafia could not agree. Choose someone to save anyway.",
      );
    }
    return newState;
  }

  // ── save → day ───────────────────────────────────────────
  if (phase === PHASES.SAVE) {
    return _resolveNightAndOpenDay(code, state);
  }

  // ── day → result ─────────────────────────────────────────
  if (phase === PHASES.DAY) {
    return _resolveDayVote(code, state);
  }

  return state;
}

// ──────────────────────────────────────────────────────────────
// Internal: resolve night kill, apply it, open day phase
// ──────────────────────────────────────────────────────────────
async function _resolveNightAndOpenDay(code, state) {
  const aliveDoctors = (state.players || []).filter((p) => p.alive && p.role === ROLES.DOCTOR);
  const saved        = aliveDoctors.length > 0
    ? resolveMajority(state.doctorVotes || {}, aliveDoctors.map((p) => p.id))
    : null;

  const killed    = state.nightKilled;
  let   players   = [...(state.players || [])];
  let   announcement, announcementId;

  if (!killed) {
    announcement   = { type: "survived", text: "The Mafia could not agree on a target. The town sleeps safely." };
    announcementId = uid();
    await pushChat(code, "all", "System", "🌅 Morning: The Mafia failed to agree. No one was killed tonight.");
  } else if (killed === saved) {
    announcement   = { type: "survived", text: "Someone was targeted — but the Doctor saved them! No one died." };
    announcementId = uid();
    await pushChat(code, "all", "System", "🌅 Morning: The Doctor saved someone! No one died tonight.");
  } else {
    const idx = players.findIndex((p) => p.id === killed);
    if (idx !== -1 && players[idx].alive) {
      players[idx] = { ...players[idx], alive: false, roleRevealed: false };
    }
    const name     = players[idx]?.name || "Someone";
    // NEVER reveal role on night kill
    announcement   = { type: "death", name, text: `${name} was found dead this morning. Their role is unknown.` };
    announcementId = uid();
    await pushChat(code, "all", "System", `🌅 Morning: ${name} was found dead. Role unknown — vote wisely.`);
  }

  const endsAt   = Date.now() + VOTE_DURATION * 1000;
  let   newState = {
    ...state,
    players,
    savedId:          saved,
    phase:            PHASES.DAY,
    dayVotes:         {},
    confirmedVotes:   [],
    skippedVotes:     [],
    timerEndsAt:      endsAt,
    timerDuration:    VOTE_DURATION,
    lastAnnouncement: announcement,
    announcementId,
    doctorSkipAt:     null,
  };

  // Check win after night kill
  const win = checkWinCondition(newState.players);
  if (win) {
    newState = { ...newState, winner: win, phase: PHASES.ENDED, timerEndsAt: null };
    await pushState(code, newState);
    return newState;
  }

  await pushState(code, newState);
  await pushChat(code, "day", "System", "☀️ Morning breaks. Discuss and vote to eliminate a suspect.");
  await pushChat(code, "all", "System", "☀️ The town awakens. Cast your suspicions in the Town Square.");
  return newState;
}

// ──────────────────────────────────────────────────────────────
// Internal: resolve day vote
// ──────────────────────────────────────────────────────────────
async function _resolveDayVote(code, state) {
  const votes       = state.dayVotes || {};
  const alivePlayers = (state.players || []).filter((p) => p.alive);
  let   players     = [...(state.players || [])];

  // Tally only alive players' votes
  const tally = {};
  alivePlayers.forEach((p) => {
    const t = votes[p.id];
    if (t) tally[t] = (tally[t] || 0) + 1;
  });

  let dayAnnouncement, dayAnnouncementId;

  if (!Object.keys(tally).length) {
    dayAnnouncement   = { type: "no-vote", text: "The town reached no consensus. No one was eliminated." };
    dayAnnouncementId = uid();
    await pushChat(code, "all", "System", "⚖️ Town Vote: No votes cast — no one eliminated.");
  } else {
    let max = 0, eliminated = null;
    for (const [id, count] of Object.entries(tally)) {
      if (count > max) { max = count; eliminated = id; }
    }

    const isTie = Object.values(tally).filter((c) => c === max).length > 1;
    if (isTie) {
      dayAnnouncement   = { type: "tie", text: "The vote was tied. No one was eliminated." };
      dayAnnouncementId = uid();
      await pushChat(code, "all", "System", "⚖️ Town Vote: Tie! No majority — no one eliminated.");
    } else {
      const idx = players.findIndex((p) => p.id === eliminated);
      if (idx !== -1 && players[idx].alive) {
        const player = players[idx];

        if (player.role === ROLES.MAFIA) {
          players[idx] = { ...player, alive: false, roleRevealed: true };
          const remainingMafia = players.filter((p) => p.alive && p.role === ROLES.MAFIA).length;

          // Only announce "MAFIA" for the LAST mafia member killed by vote
          if (remainingMafia === 0) {
            dayAnnouncement = {
              type: "mafia-caught",
              name: player.name,
              text: `${player.name} was eliminated by town vote — they were the last MAFIA! Justice prevails.`,
            };
            await pushChat(code, "all", "System",
              `⚖️ Town Vote: ${player.name} eliminated. The last Mafia is gone! ✅`);
          } else {
            // Not the last — announce as innocent (role hidden, just as with doctors)
            dayAnnouncement = {
              type: "innocent-killed",
              name: player.name,
              text: `${player.name} was eliminated by town vote — an innocent has fallen... or have they?`,
            };
            await pushChat(code, "all", "System",
              `⚖️ Town Vote: ${player.name} eliminated. They seemed suspicious... ❌`);
          }
        } else {
          // Doctor or Citizen — never reveal role
          players[idx] = { ...player, alive: false, roleRevealed: false };
          dayAnnouncement = {
            type: "innocent-killed",
            name: player.name,
            text: `${player.name} was eliminated by town vote — an innocent has fallen.`,
          };
          dayAnnouncementId = uid();
          await pushChat(code, "all", "System",
            `⚖️ Town Vote: ${player.name} eliminated. They were innocent. ❌`);
        }
        dayAnnouncementId = dayAnnouncementId || uid();
      }
    }
  }

  let newState = {
    ...state,
    players,
    dayAnnouncement:   dayAnnouncement || null,
    dayAnnouncementId: dayAnnouncementId || null,
    timerEndsAt:       null,
  };

  // Check win
  const win = checkWinCondition(newState.players);
  if (win) {
    newState = { ...newState, winner: win, phase: PHASES.ENDED };
    await pushState(code, newState);
    return newState;
  }

  newState = { ...newState, phase: PHASES.RESULT };
  await pushState(code, newState);
  // Advance from RESULT → NIGHT is handled host-side in App.jsx's state
  // listener (RESULT fallback timer). Every case — elimination, tie, no-vote
  // — uses the same exit path so RESULT never hangs.

  return newState;
}

// ══════════════════════════════════════════════════════════════
// VOTING ACTIONS (called by individual players)
// ══════════════════════════════════════════════════════════════

export async function castMafiaVote(code, state, voterId, voterName, targetId) {
  const target = (state.players || []).find((p) => p.id === targetId);
  if (!target || !target.alive) return;
  await dbUpdate(`${statePath(code)}/mafiaVotes`, { [voterId]: targetId });
  await pushChat(code, "night", "System", `${voterName} targets ${target.name}`);
}

export async function castDoctorVote(code, state, voterId, voterName, targetId) {
  const target = (state.players || []).find((p) => p.id === targetId);
  if (!target || !target.alive) return;
  await dbUpdate(`${statePath(code)}/doctorVotes`, { [voterId]: targetId });
  await pushChat(code, "save", "System", `${voterName} moves to save ${target.name}`);
}

export async function castDayVote(code, state, voterId, targetId) {
  const confirmed = (state.confirmedVotes || []);
  if (confirmed.includes(voterId)) return; // already locked
  const target = (state.players || []).find((p) => p.id === targetId);
  if (!target || !target.alive) return;
  await dbUpdate(`${statePath(code)}/dayVotes`, { [voterId]: targetId });
}

export async function confirmDayVote(code, state, voterId, voterName) {
  const myVote = (state.dayVotes || {})[voterId];
  if (!myVote) return { error: "Select a player first" };
  const confirmed = [...(state.confirmedVotes || [])];
  if (!confirmed.includes(voterId)) confirmed.push(voterId);
  await dbUpdate(statePath(code), { confirmedVotes: confirmed });
  const target = (state.players || []).find((p) => p.id === myVote);
  await pushChat(code, "day", "System", `${voterName} locks in their vote for ${target?.name || "?"}`);
}

export async function skipVote(code, state, voterId, voterName, phase) {
  // Already skipped, or already confirmed a day vote → no-op
  if ((state.skippedVotes || []).includes(voterId)) return;
  if (phase === PHASES.DAY && (state.confirmedVotes || []).includes(voterId)) return;

  const skipped = [...(state.skippedVotes || []), voterId];
  const updates = { skippedVotes: skipped };

  // Skip must cancel any pick the player already made this phase,
  // otherwise the tally would still count their earlier vote.
  if (phase === PHASES.NIGHT) {
    const next = { ...(state.mafiaVotes || {}) };
    delete next[voterId];
    updates.mafiaVotes = next;
  } else if (phase === PHASES.SAVE) {
    const next = { ...(state.doctorVotes || {}) };
    delete next[voterId];
    updates.doctorVotes = next;
  } else if (phase === PHASES.DAY) {
    const next = { ...(state.dayVotes || {}) };
    delete next[voterId];
    updates.dayVotes = next;
  }

  await dbUpdate(statePath(code), updates);

  const channel =
    phase === PHASES.NIGHT ? "night" :
    phase === PHASES.SAVE  ? "save"  : "day";
  await pushChat(code, channel, "System", `${voterName} skips their vote this round.`);
}

// ══════════════════════════════════════════════════════════════
// CHAT
// ══════════════════════════════════════════════════════════════

export async function sendChatMessage(code, channel, authorName, authorRole, text) {
  return pushChat(code, channel, authorName, text, authorRole);
}

// ══════════════════════════════════════════════════════════════
// AUTO-ADVANCE HELPERS (called by host's onValue listener)
// ══════════════════════════════════════════════════════════════

/**
 * Returns true if the host should auto-advance from night→save.
 * Condition: all alive mafia have voted OR all alive mafia have skipped.
 */
export function shouldAutoAdvanceNight(state) {
  const aliveMafia = (state.players || []).filter((p) => p.alive && p.role === ROLES.MAFIA);
  if (!aliveMafia.length) return false;
  const voted   = aliveMafia.filter((p) => (state.mafiaVotes  || {})[p.id]);
  const skipped = (state.skippedVotes || []);
  const done    = aliveMafia.filter((p) => voted.find((v) => v.id === p.id) || skipped.includes(p.id));
  return done.length >= aliveMafia.length;
}

/**
 * Returns true if the host should auto-advance from save→day.
 * Condition: all alive doctors voted/skipped OR doctorSkipAt has passed.
 */
export function shouldAutoAdvanceSave(state) {
  const aliveDoctors = (state.players || []).filter((p) => p.alive && p.role === ROLES.DOCTOR);
  if (aliveDoctors.length === 0) {
    // Check if the 10-second hidden-skip delay has passed
    return state.doctorSkipAt && Date.now() >= state.doctorSkipAt;
  }
  const skipped = state.skippedVotes || [];
  const done = aliveDoctors.filter(
    (p) => (state.doctorVotes || {})[p.id] || skipped.includes(p.id),
  );
  return done.length >= aliveDoctors.length;
}

/**
 * Returns true if the host should auto-advance from day→result.
 * Condition: all alive players have confirmed or skipped.
 */
export function shouldAutoAdvanceDay(state) {
  const alivePlayers = (state.players || []).filter((p) => p.alive);
  if (!alivePlayers.length) return false;
  const confirmed = state.confirmedVotes || [];
  const skipped   = state.skippedVotes   || [];
  const done = alivePlayers.filter(
    (p) => confirmed.includes(p.id) || skipped.includes(p.id),
  );
  return done.length >= alivePlayers.length;
}
